import store from '../redux/store';
import { logoutFetch } from '../modules/account/auth/AuthActions';
import { getProductId } from '../utils/AuthorityProvider';
import { showMessageError } from '../utils/RenderUtils';
import { getBaseUrl } from '../utils/ConfigUtils';
import * as Sentry from '@sentry/react';

// Get BASE_URL from runtime configuration (config.js)
// This allows DevOps to modify it after build without rebuilding
const getBaseUrlValue = () => getBaseUrl();

const replacer = (key, value) => {
  if (value === null || value === '') {
    return null;
  }
  return value;
};

function downloadFile(data, filename, mime) {
  const blob = new Blob([data], { type: mime || 'application/octet-stream' });
  if (typeof window.navigator.msSaveBlob !== 'undefined') {
    window.navigator.msSaveBlob(blob, filename);
  } else {
    const blobURL = window.URL.createObjectURL(blob);
    const tempLink = document.createElement('a');
    tempLink.style.display = 'none';
    tempLink.href = blobURL;
    tempLink.setAttribute('download', filename);

    if (typeof tempLink.download === 'undefined') {
      tempLink.setAttribute('target', '_blank');
    }

    document.body.appendChild(tempLink);
    tempLink.click();
    document.body.removeChild(tempLink);
    window.URL.revokeObjectURL(blobURL);
  }
}

function resolveFileName(contentDisposition) {
  const apostrophIndex = contentDisposition.lastIndexOf("'");
  if (apostrophIndex >= 0) {
    return contentDisposition.substring(
      contentDisposition.lastIndexOf("'") + 1
    );
  }
  return contentDisposition.substring(contentDisposition.lastIndexOf('=') + 1);
}

export function isAbortError(error) {
  return (
    error?.name === 'AbortError' ||
    (typeof DOMException !== 'undefined' &&
      error instanceof DOMException &&
      error.name === 'AbortError')
  );
}

class BaseApi {
  static get errors() {
    return {
      UNKNOWN: 'An unknown error occured.',
      UNAUTHORIZED: 'Unauthorized: You need to log in',
      FORBIDDEN: 'Access denied! You do not have permission to view this page.',
      NOT_FOUND: 'Oops! The page you’re looking for can’t be found.',
      ABORTION:
        'The request was aborted due to a timeout or user action. Please try again.',
      NOT_ACCEPTABLE:
        'The requested resource is not available in a format that your browser can understand.',
      INTERNAL:
        'There was an error communicating with the server. Please ensure your internet connection is stable and try again.',
    };
  }

  static async getImage(url, headerTitles) {
    const abortController = new AbortController();

    try {
      const response = await fetch(getBaseUrlValue() + url, {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        switch (response.status) {
          case 401:
            store.dispatch(logoutFetch());
            throw new Error(this.errors.UNAUTHORIZED);
          case 404:
            throw new Error(this.errors.NOT_FOUND);
          case 403:
            throw new Error(this.errors.FORBIDDEN);
          case 406: {
            const errorData = await response.json();

            if (Array.isArray(errorData.errors)) {
              throw errorData;
            }
            throw new Error(this.errors.NOT_ACCEPTABLE);
          }
          case 502:
          case 503:
          case 504:
            throw new Error(this.errors.INTERNAL);
          default:
            abortController.abort(this.errors.ABORTION);
            throw new Error(this.errors.UNKNOWN);
        }
      }

      let customHeaders = {};

      const file = await response.blob();

      headerTitles.forEach((title) => {
        customHeaders[title] = response.headers.get(title);
      });

      const imageUrl = URL.createObjectURL(file);

      return { imageUrl, customHeaders };
    } catch (error) {
      BaseApi.reportToSentry(error, url, 'POST');
      showMessageError(error);
      throw error;
    }
  }

  static async postForFile(url, data) {
    const abortController = new AbortController();

    try {
      const response = await fetch(getBaseUrlValue() + url, {
        method: 'POST',
        body: JSON.stringify(data, replacer),
        headers: getHeaders(),
        credentials: 'include',
        signal: abortController.signal,
      });

      if (!response.ok) {
        abortController.abort(this.errors.ABORTION);
        throw new Error(this.errors.INTERNAL);
      }

      const mime = response.headers.get('content-type');

      const filename = resolveFileName(
        response.headers.get('content-disposition')
      );

      const file = await response.blob();

      downloadFile(file, filename, mime);

      return response;
    } catch (error) {
      BaseApi.reportToSentry(error, url, 'POST');
      showMessageError(error);
      throw error;
    }
  }

  static async uploadFile(url, file) {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(getBaseUrlValue() + url, {
        method: 'POST',
        credentials: 'include',
        headers: getHeadersUploadingFile(),
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();

        if (Array.isArray(errorData.errors)) {
          throw errorData;
        }
        const error = new Error(`Upload failed: ${response.statusText}`);

        error.response = response;

        throw error;
      }

      const data = await response.json();

      return data.result ?? {};
    } catch (error) {
      BaseApi.reportToSentry(error, url, 'POST');
      showMessageError(error);
      throw error;
    }
  }

  static async uploadEmailImage(url, file) {
    const result = new FormData();

    result.append('file', file.attachments[0]);

    try {
      const response = await fetch(getBaseUrlValue() + url, {
        method: 'POST',
        credentials: 'include',
        headers: getHeadersUploadingFile(),
        body: result,
      });

      if (!response.ok) {
        const errorData = await response.json();

        if (Array.isArray(errorData.errors)) {
          throw errorData;
        }
        const error = new Error(`Upload failed: ${response.statusText}`);

        error.response = response;

        throw error;
      }

      const data = await response.json();

      return data.result.path;
    } catch (error) {
      BaseApi.reportToSentry(error, url, 'POST');
      showMessageError(error);
      throw error;
    }
  }

  static async post(url, payload = {}, options = {}) {
    const abortController = new AbortController();
    const signal = options.signal || abortController.signal;

    try {
      const response = await fetch(getBaseUrlValue() + url, {
        method: 'POST',
        body: JSON.stringify(payload, replacer),
        signal,
        headers: getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        switch (response.status) {
          case 401:
            store.dispatch(logoutFetch());
            throw new Error(this.errors.UNAUTHORIZED);
          case 404:
            throw new Error(this.errors.NOT_FOUND);
          case 403:
            throw new Error(this.errors.FORBIDDEN);
          case 406: {
            const errorData = await response.json();

            if (Array.isArray(errorData.errors)) {
              throw errorData;
            }
            throw new Error(this.errors.NOT_ACCEPTABLE);
          }
          case 502:
          case 503:
          case 504:
            throw new Error(this.errors.INTERNAL);
          default:
            abortController.abort(this.errors.ABORTION);
            throw new Error(this.errors.UNKNOWN);
        }
      }

      const data = await response.json();

      return data.result ?? {};
    } catch (error) {
      if (isAbortError(error)) {
        throw error;
      }
      BaseApi.reportToSentry(error, url, 'POST');
      showMessageError(error);
      throw error;
    }
  }

  static async get(url, payload) {
    const abortController = new AbortController();

    try {
      const response = await fetch(getBaseUrlValue() + url, {
        method: 'GET',
        body: JSON.stringify(payload, replacer),
        headers: getHeaders(),
        credentials: 'include',
        signal: abortController.signal,
      });

      if (!response.ok) {
        switch (response.status) {
          case 401:
            store.dispatch(logoutFetch());
            throw new Error(this.errors.UNAUTHORIZED);
          case 404:
            throw new Error(this.errors.NOT_FOUND);
          case 403:
            throw new Error(this.errors.FORBIDDEN);
          case 406: {
            const errorData = await response.json();

            if (Array.isArray(errorData.errors)) {
              throw errorData;
            }
            throw new Error(this.errors.NOT_ACCEPTABLE);
          }
          case 502:
          case 503:
          case 504:
            throw new Error(this.errors.INTERNAL);
          default:
            abortController.abort(this.errors.ABORTION);
            throw new Error(this.errors.UNKNOWN);
        }
      }

      const data = await response.json();

      return data.result ?? {};
    } catch (error) {
      BaseApi.reportToSentry(error, url, 'GET');
      showMessageError(error);
      throw error;
    }
  }

  static async put(url, payload) {
    const abortController = new AbortController();

    try {
      const response = await fetch(getBaseUrlValue() + url, {
        method: 'PUT',
        body: JSON.stringify(payload, replacer),
        headers: getHeaders(),
        credentials: 'include',
        signal: abortController.signal,
      });

      if (!response.ok) {
        switch (response.status) {
          case 401:
            store.dispatch(logoutFetch());
            throw new Error(this.errors.UNAUTHORIZED);
          case 404:
            throw new Error(this.errors.NOT_FOUND);
          case 403:
            throw new Error(this.errors.FORBIDDEN);
          case 406: {
            const errorData = await response.json();

            if (Array.isArray(errorData.errors)) {
              throw errorData;
            }
            throw new Error(this.errors.NOT_ACCEPTABLE);
          }
          case 502:
          case 503:
          case 504:
            throw new Error(this.errors.INTERNAL);
          default:
            abortController.abort(this.errors.ABORTION);
            throw new Error(this.errors.UNKNOWN);
        }
      }

      const data = await response.json();

      return data.result ?? {};
    } catch (error) {
      BaseApi.reportToSentry(error, url, 'PUT');
      showMessageError(error);
      throw error;
    }
  }

  static async delete(url, payload) {
    const abortController = new AbortController();

    try {
      const response = await fetch(getBaseUrlValue() + url, {
        method: 'DELETE',
        body: JSON.stringify(payload, replacer),
        headers: getHeaders(),
        credentials: 'include',
        signal: abortController.signal,
      });

      if (!response.ok) {
        switch (response.status) {
          case 401:
            store.dispatch(logoutFetch());
            throw new Error(this.errors.UNAUTHORIZED);
          case 404:
            throw new Error(this.errors.NOT_FOUND);
          case 403:
            throw new Error(this.errors.FORBIDDEN);
          case 406: {
            const errorData = await response.json();

            if (Array.isArray(errorData.errors)) {
              throw errorData;
            }
            throw new Error(this.errors.NOT_ACCEPTABLE);
          }
          case 502:
          case 503:
          case 504:
            throw new Error(this.errors.INTERNAL);
          default:
            abortController.abort(this.errors.ABORTION);
            throw new Error(this.errors.UNKNOWN);
        }
      }

      const data = await response.json();

      return data.result ?? {};
    } catch (error) {
      BaseApi.reportToSentry(error, url, 'DELETE');
      showMessageError(error);
      throw error;
    }
  }

  static async downLoadFile(url) {
    const abortController = new AbortController();

    const currentUser = store.getState().account.auth.currentUser;
    const pid =
      (currentUser && currentUser.login && currentUser.lastProduct) ||
      (currentUser.products &&
        Array.isArray(currentUser.products) &&
        currentUser.products.length > 0 &&
        currentUser.products[0].id);

    try {
      const response = await fetch(getBaseUrlValue() + url, {
        method: 'GET',
        headers: new Headers({
          'Content-Type': 'application/json',
          'PRODUCT-ID': pid,
        }),
        credentials: 'include',
        signal: abortController.signal,
      });

      if (!response.ok) {
        switch (response.status) {
          case 401:
            store.dispatch(logoutFetch());
            throw new Error(this.errors.UNAUTHORIZED);
          case 404:
            throw new Error(this.errors.NOT_FOUND);
          case 403:
            throw new Error(this.errors.FORBIDDEN);
          case 406: {
            const errorData = await response.json();

            if (Array.isArray(errorData.errors)) {
              throw errorData;
            }
            throw new Error(this.errors.NOT_ACCEPTABLE);
          }
          case 502:
          case 503:
          case 504:
            throw new Error(this.errors.INTERNAL);
          default:
            abortController.abort(this.errors.ABORTION);
            throw new Error(this.errors.UNKNOWN);
        }
      }

      const file = await response.blob();

      const href = URL.createObjectURL(file);

      const a = document.createElement('a');

      const content = response.headers.get('content-disposition');

      if (content) {
        a.href = href;
        a.download = content.replace("filename*=UTF-8''", '');
        document.body.appendChild(a);
        a.click();
        a.remove();
      }

      return response;
    } catch (error) {
      BaseApi.reportToSentry(error, url, 'GET');
      showMessageError(error);
      throw error;
    }
  }

  static reportToSentry(error, url, method) {
    if (window.location.host !== 'dash.intrack.ir') {
      return;
    }
    if (Array.isArray(error?.errors)) {
      return;
    }
    const { UNAUTHORIZED, FORBIDDEN, NOT_FOUND, NOT_ACCEPTABLE } =
      BaseApi.errors;
    if (
      error instanceof Error &&
      [UNAUTHORIZED, FORBIDDEN, NOT_FOUND, NOT_ACCEPTABLE].some((msg) =>
        error.message.includes(msg)
      )
    ) {
      return;
    }
    const exception = error instanceof Error ? error : new Error(String(error));
    Sentry.withScope((scope) => {
      scope.setTag('api.url', url);
      scope.setTag('api.method', method);
      Sentry.captureException(exception);
    });
  }
}

function getHeaders() {
  let pid = getProductId(store.getState().router.location.pathname);
  let headers = new Headers({
    'Content-Type': 'application/json',
  });
  if (pid) {
    headers.append('PRODUCT-ID', pid);
  }
  return headers;
}

function getHeadersUploadingFile() {
  let pid = getProductId(store.getState().router.location.pathname);
  let headers = new Headers({});
  if (pid) {
    headers.append('PRODUCT-ID', pid);
  }
  return headers;
}

export default BaseApi;
